import { FC, useEffect } from 'react'
import { Link, Outlet, useNavigate } from 'react-router-dom'
import { useUser } from '@clerk/clerk-react'
import { isNonEmptyString } from '@utils/type-checkers'
import * as Sentry from "@sentry/react"
import './group.css'

// COMPONENTS
import * as Icons from '@icons'
import { Column } from '@components/templates/column'
import Toast from '@components/molecules/toast'

// ROUTES
import CreateGroupRoute from './create'
import GroupIdRoute from './[groupId]'
import RecreateGroupRoute from './recreate'

// TYPES
import type { RouteObject } from 'react-router-dom'

// UTILITIES
import { GroupContext } from './_localStore/reducer'
import { useGroupReducer } from './_localStore/_hooks'
import { createAuthenticatedLoader } from '@utils/loaders'


export type GroupScreen = typeof groupScreens[number]
export const groupScreens = ["status", "create", "pledge"] as const

const Layout: FC = () => {
    const groupReducer = useGroupReducer()
    const { toast } = groupReducer.localState
    const { isLoaded, user } = useUser()
    const navigate = useNavigate()

    useEffect(() => {
        if (isLoaded && user) {
            if (!isNonEmptyString(user.firstName) || !isNonEmptyString(user.lastName)) {
                Sentry.addBreadcrumb({ category: 'group', message: 'missing name, redirecting to /sign-up/add-info', level: 'info' })
                navigate(`/sign-up/add-info${document.location.search}`, { replace: true })
            }
        }
    }, [isLoaded, user, navigate])

    return (
        <GroupContext.Provider value={groupReducer}>
            <Column className="group-stack">
                <Column.Header.End>
                    <Link className="account-redirect-container" to="/account">
                        <Icons.House tint="var(--splitcard-black)" />
                    </Link>
                </Column.Header.End>
                <Outlet />
                <div style={{ position: 'fixed', left: 0, right: 0, bottom: 'var(--header-height)', zIndex: 100, pointerEvents: 'none' }}>
                    <Toast toast={toast} />
                </div>
            </Column>
        </GroupContext.Provider>
    )
}

const loader = createAuthenticatedLoader(() => null)

const GroupRoute: RouteObject = {
    path: "group",
    element: <Layout />,
    errorElement: null,
    loader,
    shouldRevalidate: () => false,
    children: [
        RecreateGroupRoute,
        GroupIdRoute,
        CreateGroupRoute,
    ],
}

export default GroupRoute
